import type { ReactNode } from 'react';

export interface ProgressDetails {
  completed: boolean;
  percentage: number;
}

export const progressDetails = (value: number, max = 100, completionThreshold = 1): ProgressDetails => {
  const ratio = Number.isFinite(value) && Number.isFinite(max) && max > 0 ? value / max : 0;
  const normalized = Math.min(Math.max(ratio, 0), 1);
  return {
    completed: normalized > completionThreshold,
    percentage: Math.round(normalized * 100),
  };
};

interface ProgressBarProps {
  className?: string;
  completedLabel?: ReactNode;
  completionThreshold?: number;
  label?: ReactNode | ((details: ProgressDetails) => ReactNode);
  max?: number;
  size?: 'small' | 'medium';
  value: number;
}

export const ProgressBar = ({
  className = '',
  completedLabel,
  completionThreshold = 1,
  label,
  max = 100,
  size = 'small',
  value,
}: ProgressBarProps) => {
  const normalizedMax = Number.isFinite(max) && max > 0 ? max : 100;
  const normalizedValue = Number.isFinite(value) ? Math.min(Math.max(value, 0), normalizedMax) : 0;
  const details = progressDetails(normalizedValue, normalizedMax, completionThreshold);
  const displayLabel = details.completed ? completedLabel : typeof label === 'function' ? label(details) : label;
  return (
    <span
      className={`progress-bar ${size} ${details.completed ? 'completed' : ''} ${className}`.trim()}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={normalizedMax}
      aria-valuenow={normalizedValue}
    >
      <span className="progress-bar-track">
        <span style={{ width: `${details.percentage}%` }} />
      </span>
      {displayLabel !== undefined ? <span className="progress-bar-label">{displayLabel}</span> : null}
    </span>
  );
};
